import { MaterialIcons } from "@expo/vector-icons";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { BackHandler, StyleSheet, View } from "react-native";
import { useTheme } from "react-native-elements";
import { sortInventory } from "../backend/items/Inventory";
import { isCraftingUnlocked } from "../backend/items/Items";
import { PlayerContext } from "../backend/PlayerContext";
import Crafting from "./inventory/Crafting";
import { EquipmentSetButtons } from "./inventory/EquipmentSetButtons";
import EquippedItems from "./inventory/EquippedItems";
import InventoryItems from "./inventory/InventoryItems";
import { Button } from "./utility/Button";
import { Text } from "./utility/Text";
import { VisibilityContext } from "./utility/VisibilityContext";

export default function Inventory() {
  const [isCrafting, setIsCrafting] = useState(false);
  const theme = useTheme();

  const playerContext = useContext(PlayerContext);
  const playerContextRef = useRef(playerContext);
  playerContextRef.current = playerContext;

  const sortInventoryCallback = useCallback(() => {
    playerContextRef.current.apply(sortInventory);
  }, []);

  const backHandler = useCallback(() => {
    if (isCrafting) {
      setIsCrafting(false);
      return true;
    }
    return false;
  }, [isCrafting, setIsCrafting]);

  useEffect(() => {
    const handler = BackHandler.addEventListener(
      "hardwareBackPress",
      backHandler,
    );

    return () => handler.remove();
  }, [backHandler]);

  const visible = useContext(VisibilityContext);
  useEffect(() => {
    if (!visible) {
      setIsCrafting(false);
    }
  }, [visible]);

  if (!isCrafting) {
    return (
      <View style={styles.container}>
        <View style={styles.fullBleedPadding}>
          <View style={styles.topRow}>
            <Text h2>Inventory</Text>
            <View style={styles.topButtons}>
              <Button
                title="Sort Inventory"
                onPress={sortInventoryCallback}
                titleStyle={styles.smallButtonTitle}
              />
              {isCraftingUnlocked(playerContext) && (
                <Button
                  title="Craft Items"
                  containerStyle={styles.craftButton}
                  onPress={() => setIsCrafting(true)}
                />
              )}
            </View>
          </View>
          <View style={styles.equippedItemsContainer}>
            <EquippedItems />
          </View>
          <View style={styles.equipmentSetButtonsRow}>
            <EquipmentSetButtons />
          </View>
        </View>
        <InventoryItems isFullBleed={true} />
      </View>
    );
  } else {
    return (
      <View style={styles.container} onAccessibilityEscape={backHandler}>
        <View style={[styles.topRow, styles.row, styles.fullBleedPadding]}>
          <MaterialIcons
            name="arrow-back"
            accessibilityLabel="Back to Inventory"
            color={theme.theme.colors.black}
            size={24}
            style={styles.arrowBack}
            onPress={() => setIsCrafting(false)}
            hitSlop={{ top: 30, left: 30, bottom: 30, right: 30 }}
          />
          <Text h2>Crafting</Text>
        </View>
        <Crafting />
      </View>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: "stretch",
    justifyContent: "flex-start",
    width: "100%",
  },
  topRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
    height: 48,
  },
  arrowBack: {
    marginRight: 8,
  },
  equippedItemsContainer: {
    marginBottom: 8,
  },
  row: {
    justifyContent: "flex-start",
  },
  topButtons: {
    flexDirection: "row",
    alignItems: "center",
  },
  craftButton: {
    marginLeft: 16,
  },
  fullBleedPadding: {
    paddingHorizontal: 8,
    marginTop: 8,
  },
  equipmentSetButtonsRow: {
    marginBottom: 16,
  },
  smallButtonTitle: {
    fontSize: 12,
  },
});
